<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { AlertCircle } from 'lucide-vue-next'
import mermaid from 'mermaid'
import { torcazaMermaidConfig } from '@/config/mermaidTheme'

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const error = ref('')
const internalCode = ref(props.modelValue)

let validateTimeout: ReturnType<typeof setTimeout> | null = null

const validateCode = async (code: string) => {
  if (!code.trim()) {
    error.value = ''
    return
  }
  try {
    error.value = ''
    // Extraer código puro si tiene bloques markdown
    let rawCode = code
    const match = code.match(/```mermaid\n([\s\S]*?)```/)
    if (match) {
      rawCode = match[1]
    } else {
      rawCode = code.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '')
    }
    
    // Mermaid necesita inicializarse antes del parse
    mermaid.initialize(torcazaMermaidConfig)
    await mermaid.parse(rawCode)
  } catch (e: any) {
    error.value = e.message || 'Sintaxis de Mermaid inválida'
  }
}

onMounted(() => {
  validateCode(internalCode.value)
})

watch(() => props.modelValue, (newVal) => {
  if (internalCode.value !== newVal) {
    internalCode.value = newVal
    validateCode(newVal)
  }
})

const onInput = (e: Event) => {
  const target = e.target as HTMLTextAreaElement
  internalCode.value = target.value
  emit('update:modelValue', target.value)
  
  if (validateTimeout) clearTimeout(validateTimeout)
  validateTimeout = setTimeout(() => {
    validateCode(target.value)
  }, 400)
}
</script>

<template>
  <div class="relative flex-1 w-full h-full flex flex-col bg-transparent overflow-hidden">
    <textarea
      :value="internalCode"
      @input="onInput"
      class="flex-1 w-full h-full p-4 resize-none outline-none font-mono text-xs text-foreground bg-transparent focus:ring-2 focus:ring-inset focus:ring-primary"
      spellcheck="false"
      placeholder="Escribe tu código Mermaid aquí..."
    ></textarea>
    
    <!-- Panel de Errores Estilo IDE -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div 
        v-if="error" 
        class="absolute bottom-0 left-0 right-0 p-3 bg-destructive/10 border-t border-destructive/20 text-destructive text-sm flex items-start gap-2 max-h-[60%] overflow-auto backdrop-blur-sm shadow-[0_-10px_15px_-3px_rgba(0,0,0,0.1)] z-10"
      >
        <AlertCircle class="w-4 h-4 mt-0.5 shrink-0" />
        <pre class="whitespace-pre-wrap font-sans text-xs flex-1 leading-tight">{{ error }}</pre>
      </div>
    </transition>
  </div>
</template>
